import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '@/components/Header';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import HowToUseSection from '@/components/HowToUseSection';
import { HELP_URLS } from '@/components/HelpBenefits';
import WhatsNewSection from '@/components/WhatsNewSection';
import { ArrowLeft, TrendingUp, ChevronDown, ChevronUp, BookOpen, AlertTriangle, Clock, Target, BarChart3, Brain, Zap, TrendingDown, ShieldAlert, Crosshair, SlidersHorizontal } from 'lucide-react';

export default function GrowthModelBenefits() {
  const navigate = useNavigate();
  const [isBenefitsOpen, setIsBenefitsOpen] = useState(true);

  return (
    <div className="min-h-screen bg-[#F3F4F6] dark:bg-[#0b1220] transition-colors duration-200">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate('/growth-model')}
          className="mb-6 text-[#0891b2] hover:text-[#0e7490] hover:bg-[#0891b2]/10"
          data-testid="back-to-growth-model"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Growth vs Model Analysis
        </Button>

        {/* Main Content Card */}
        <Card className="border-slate-200 shadow-lg">
          <CardHeader className="bg-gradient-to-r from-[#0891b2] to-[#0e7490] text-white rounded-t-lg px-4 py-3 sm:px-6 sm:py-4">
            <CardTitle className="text-lg sm:text-2xl flex items-center gap-2 sm:gap-3">
              <TrendingUp className="h-5 w-5 sm:h-7 sm:w-7" />
              Benefits of Growth vs Model Analysis
            </CardTitle>
          </CardHeader>
          
          <CardContent className="p-4 sm:p-6 space-y-4">
            
            <WhatsNewSection />

            {/* Learn the Benefits Section - Collapsible */}
            <Collapsible open={isBenefitsOpen} onOpenChange={setIsBenefitsOpen}>
              <CollapsibleTrigger asChild>
                <button 
                  className="w-full flex items-center justify-between p-3 sm:p-4 bg-[#0891b2]/5 border border-[#0891b2]/20 rounded-lg hover:bg-[#0891b2]/10 transition-colors"
                  data-testid="benefits-toggle"
                >
                  <div className="flex items-center gap-2 sm:gap-3">
                    <div className="p-2 bg-[#0891b2]/10 rounded-full">
                      <BookOpen className="h-4 w-4 sm:h-5 sm:w-5 text-[#0891b2]" />
                    </div>
                    <span className="font-semibold text-[#0891b2] text-sm sm:text-base">Learn the Benefits of Growth vs Model Analysis</span>
                  </div>
                  {isBenefitsOpen ? (
                    <ChevronUp className="h-5 w-5 text-[#0891b2]" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-[#0891b2]" />
                  )}
                </button>
              </CollapsibleTrigger>
              
              <CollapsibleContent className="mt-3 space-y-4">
                {/* Introduction */}
                <section className="bg-slate-50 border border-slate-200 rounded-lg p-4 sm:p-5">
                  <p className="text-xs sm:text-sm text-gray-700 leading-relaxed">
                    Growth vs Model Analysis allows producers to clearly understand how their fish are performing relative to their biological growth potential. By comparing actual sampled weights against a scientifically derived growth model (SGR- and temperature-based, now extended to 800g), the tool transforms raw sampling data into a meaningful performance benchmark with integrated Missing Fish Intelligence.
                  </p>
                </section>

                {/* NEW: Missing Fish Intelligence Section */}
                <section className="bg-purple-50 border border-purple-200 rounded-lg p-4 sm:p-5">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-purple-100 rounded-full flex-shrink-0">
                      <Brain className="h-4 w-4 text-purple-600" />
                    </div>
                    <div>
                      <h4 className="text-xs sm:text-sm font-semibold text-purple-800 mb-2">Missing Fish Intelligence</h4>
                      <p className="text-xs sm:text-sm text-gray-700 leading-relaxed mb-3">
                        Our unified detection engine analyzes your sample data using multiple methods to estimate missing fish with SSOT confidence scoring — all sections display the same resolved detection strength (High/Moderate/Low):
                      </p>
                      <ul className="space-y-2 text-xs sm:text-sm text-gray-700">
                        <li className="flex items-start gap-2">
                          <Zap className="h-4 w-4 text-purple-600 mt-0.5 flex-shrink-0" />
                          <span><strong>Sample Jump Analysis:</strong> Detects sudden upward weight jumps between samples that indicate fish loss events. Uses a gap-weighted blend of trajectory projection and the adjusted model baseline to avoid overestimating losses in sparse datasets, and outputs a conservative estimate with a full range</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <TrendingDown className="h-4 w-4 text-purple-600 mt-0.5 flex-shrink-0" />
                          <span><strong>FCR-Based Detection:</strong> When feed data is available, compares biological FCR against model expectations to identify when fewer fish may be consuming the feed</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Crosshair className="h-4 w-4 text-purple-600 mt-0.5 flex-shrink-0" />
                          <span><strong>Growth-Fit Analysis:</strong> Uses R² curve fitting to determine the fish count that best matches observed growth patterns</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <ShieldAlert className="h-4 w-4 text-purple-600 mt-0.5 flex-shrink-0" />
                          <span><strong>Conservative Planning Estimate:</strong> When multiple methods disagree, the lower (more conservative) estimate is recommended as the planning figure — reducing the risk of overstating losses</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </section>

                {/* NEW: Best Fit Adjusted Model Section */}
                <section className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 sm:p-5">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-emerald-100 rounded-full flex-shrink-0">
                      <SlidersHorizontal className="h-4 w-4 text-emerald-600" />
                    </div>
                    <div>
                      <h4 className="text-xs sm:text-sm font-semibold text-emerald-800 mb-2">Best Fit Adjusted Model</h4>
                      <p className="text-xs sm:text-sm text-gray-700 leading-relaxed mb-3">
                        The system automatically calibrates a global scaling percentage that adjusts the standard growth model to best fit your actual sample data. This adjusted baseline provides a more accurate reference for all downstream analysis:
                      </p>
                      <ul className="space-y-2 text-xs sm:text-sm text-gray-700">
                        <li className="flex items-start gap-2">
                          <span className="text-emerald-600 font-bold mt-0.5">1.</span>
                          <span><strong>Auto-Calibrated Scaling:</strong> A single percentage (e.g., "Model adjusted to 71%") is computed via weighted least squares regression across all samples, giving greater weight to larger (later) samples for a realistic baseline</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-emerald-600 font-bold mt-0.5">2.</span>
                          <span><strong>Live Dynamic Effect:</strong> The adjusted baseline directly influences jump detection — changing the scaling recalculates expected weights, jump ratios, and missing fish estimates in real time, without needing to re-run the analysis</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-emerald-600 font-bold mt-0.5">3.</span>
                          <span><strong>Manual Override Slider:</strong> A manual control lets you explore "what-if" scenarios by adjusting the model percentage and instantly seeing the impact on both the chart and the missing fish estimates</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-emerald-600 font-bold mt-0.5">4.</span>
                          <span><strong>Fit Quality Metrics:</strong> R², RMSE, valid sample count, and excluded sample count are displayed so you can assess how well the model fits your data</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </section>

                {/* NEW: Conservative Estimation for Sparse Data */}
                <section className="bg-orange-50 border border-orange-200 rounded-lg p-4 sm:p-5">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-orange-100 rounded-full flex-shrink-0">
                      <ShieldAlert className="h-4 w-4 text-orange-600" />
                    </div>
                    <div>
                      <h4 className="text-xs sm:text-sm font-semibold text-orange-800 mb-2">Conservative Estimation for Sparse Data</h4>
                      <p className="text-xs sm:text-sm text-gray-700 leading-relaxed mb-3">
                        In real-world aquaculture, sampling gaps of weeks or months are common. Pure trajectory projection over long gaps can overestimate losses. The system addresses this with:
                      </p>
                      <ul className="space-y-2 text-xs sm:text-sm text-gray-700">
                        <li className="flex items-start gap-2">
                          <span className="text-orange-600 font-bold mt-0.5">1.</span>
                          <span><strong>Gap-Weighted Blending:</strong> Short gaps (&lt;30 days) trust actual trajectory 100%. Longer gaps progressively blend in the adjusted model baseline — giving up to 70% model weight for gaps over 150 days</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-orange-600 font-bold mt-0.5">2.</span>
                          <span><strong>Estimate Ranges:</strong> Instead of a single precise number, the system shows a conservative-to-aggressive range (e.g., "18,997 — 42,779"), so you can plan based on the realistic spread</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-orange-600 font-bold mt-0.5">3.</span>
                          <span><strong>Quality Indicators:</strong> Each estimate is tagged with a quality rating (Good / Moderate / Low confidence) based on sample gap length and fit error, so you know when to trust the numbers and when to sample more</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </section>

                {/* Benefit 1: Early Identification */}
                <section className="bg-amber-50 border border-amber-200 rounded-lg p-4 sm:p-5">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-amber-100 rounded-full flex-shrink-0">
                      <AlertTriangle className="h-4 w-4 text-amber-600" />
                    </div>
                    <div>
                      <h4 className="text-xs sm:text-sm font-semibold text-amber-800 mb-2">Early Identification of Performance Deviations</h4>
                      <p className="text-xs sm:text-sm text-gray-700 leading-relaxed mb-2">
                        A key benefit is early identification of growth underperformance or overperformance. When actual growth falls below the model, it can indicate limiting factors such as suboptimal feeding practices, health challenges, environmental stress, or density-related constraints.
                      </p>
                      <p className="text-xs sm:text-sm text-gray-700 leading-relaxed mb-2">
                        Conversely, when growth exceeds the model, the system provides clear indicators distinguishing between exceptional rearing conditions and potential stock number inaccuracies through SSOT-aligned severity alerts (High, Moderate, or Low detection strength).
                      </p>
                      <p className="text-xs sm:text-sm text-gray-700 leading-relaxed">
                        This early detection capability is vital for timely intervention—allowing farm managers to adjust feeding regimes, investigate potential health issues, or optimise stocking densities before small deviations compound into significant production losses.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Benefit 2: Context Between Samples */}
                <section className="bg-blue-50 border border-blue-200 rounded-lg p-4 sm:p-5">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-blue-100 rounded-full flex-shrink-0">
                      <Clock className="h-4 w-4 text-blue-600" />
                    </div>
                    <div>
                      <h4 className="text-xs sm:text-sm font-semibold text-blue-800 mb-2">Context Between Samples with Loss Window Detection</h4>
                      <p className="text-xs sm:text-sm text-gray-700 leading-relaxed mb-2">
                        The analysis provides context between samples. Rather than treating each sample as an isolated data point, Growth vs Model reconstructs the expected growth trajectory over time, helping users understand when and how deviations occurred.
                      </p>
                      <p className="text-xs sm:text-sm text-gray-700 leading-relaxed mb-2">
                        When a sample jump is detected, the system identifies the <em>probable loss window</em>—the specific date range when fish loss likely occurred—and displays it directly on the growth chart for visual correlation with operational events.
                      </p>
                      <p className="text-xs sm:text-sm text-gray-700 leading-relaxed">
                        By visualising the growth path between sampling events with annotated loss windows, managers can pinpoint specific periods where growth accelerated or lagged, enabling more precise root cause analysis and targeted corrective actions.
                      </p>
                    </div>
                  </div>
                </section>

                {/* NEW: Intelligent Analysis Features */}
                <section className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 sm:p-5">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-indigo-100 rounded-full flex-shrink-0">
                      <BarChart3 className="h-4 w-4 text-indigo-600" />
                    </div>
                    <div>
                      <h4 className="text-xs sm:text-sm font-semibold text-indigo-800 mb-2">Advanced Analytical Features</h4>                      <ul className="space-y-2 text-xs sm:text-sm text-gray-700">
                        <li className="flex items-start gap-2">
                          <span className="text-indigo-600 font-bold">•</span>
                          <span><strong>Growth Intelligence Summary:</strong> Consolidated view showing growth direction (above/below model), confidence level, and missing fish assessment in one glance</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-indigo-600 font-bold">•</span>
                          <span><strong>Per-Sample Deviation Analysis:</strong> Table showing each sample's deviation from model with pattern classification (stable, improving, declining)</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-indigo-600 font-bold">•</span>
                          <span><strong>FCR vs Jump Side-by-Side Comparison:</strong> Both detection methods shown together with the conservative estimate highlighted as the recommended planning figure</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-indigo-600 font-bold">•</span>
                          <span><strong>Live Adjusted Baseline:</strong> The adjusted model percentage dynamically updates jump detection — move the slider and watch the expected weight, jump ratio, and missing fish recalculate instantly</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-indigo-600 font-bold">•</span>
                          <span><strong>Confidence Scoring:</strong> Each detection method provides a confidence rating to help you prioritize which signals to act on</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-indigo-600 font-bold">•</span>
                          <span><strong>Assumed Fish Number Control:</strong> Manually adjust the assumed fish count and instantly see the impact on growth curves and FCR calculations</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-indigo-600 font-bold">•</span>
                          <span><strong>Cross-Origin FCR Versions:</strong> Select any FCR multiplier version from any genetic origin — not restricted to the current species, enabling flexible benchmarking</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </section>

                {/* Benefit 3: Confident Decision-Making */}
                <section className="bg-green-50 border border-green-200 rounded-lg p-4 sm:p-5">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-green-100 rounded-full flex-shrink-0">
                      <Target className="h-4 w-4 text-green-600" />
                    </div>
                    <div>
                      <h4 className="text-xs sm:text-sm font-semibold text-green-800 mb-2">Confident Decision-Making</h4>
                      <p className="text-xs sm:text-sm text-gray-700 leading-relaxed mb-2">
                        From a management perspective, Growth vs Model Analysis supports more confident decision-making. It helps validate whether production is tracking according to plan, whether corrective action is required, or whether observed results are simply normal biological variation.
                      </p>
                      <p className="text-xs sm:text-sm text-gray-700 leading-relaxed mb-2">
                        The system clearly indicates when FCR or Sample Jump analysis is the "primary" detection method, and when the other is "secondary"—reducing confusion about which signal to prioritize. All severity labels are resolved through a Single Source of Truth (SSOT) so every section of the report displays the same detection strength.
                      </p>
                      <p className="text-xs sm:text-sm text-gray-700 leading-relaxed">
                        This reduces reliance on intuition and replaces it with objective, model-based evidence. The ability to distinguish between genuine performance issues and natural variation prevents overreaction to normal fluctuations while ensuring genuine problems are addressed promptly.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Conclusion: Performance Scorecard */}
                <section className="bg-[#0891b2]/5 border border-[#0891b2]/20 rounded-lg p-4 sm:p-5">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-[#0891b2]/10 rounded-full flex-shrink-0">
                      <BarChart3 className="h-4 w-4 text-[#0891b2]" />
                    </div>
                    <div>
                      <h4 className="text-xs sm:text-sm font-semibold text-[#0891b2] mb-2">Performance Scorecard for Production</h4>
                      <p className="text-xs sm:text-sm text-gray-700 leading-relaxed mb-2">
                        Ultimately, Growth vs Model Analysis acts as a performance scorecard for the production cycle—quantifying how closely real-world growth aligns with biological expectations (now extended to 800g for larger fish), improving transparency, and enabling farms to optimise feeding strategies, production timelines, and overall efficiency.
                      </p>
                      <p className="text-xs sm:text-sm text-gray-700 leading-relaxed">
                        This quantified approach to performance measurement establishes clear benchmarks for continuous improvement, with integrated Missing Fish Intelligence giving you confidence in your stock numbers alongside your growth metrics.
                      </p>
                    </div>
                  </div>
                </section>
              </CollapsibleContent>
            </Collapsible>

            {/* How to Use Section - Standardized Component */}
            <HowToUseSection 
              featureName="Growth vs Model Analysis"
              youtubeUrl={HELP_URLS.growthModelAnalysis.youtube}
            />

            {/* CTA */}
            <div className="pt-4 border-t border-slate-200 flex justify-center">
              <Button
                onClick={() => navigate('/growth-model')}
                className="bg-[#0891b2] hover:bg-[#0e7490] text-white px-6 sm:px-8 py-2 sm:py-3 text-sm sm:text-base"
                data-testid="start-growth-model-btn"
              >
                <TrendingUp className="h-4 w-4 mr-2" />
                Start Growth vs Model Analysis
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
